const API_BASE = '/api'

function workspacePath(workspaceKey: string, suffix = ''): string {
  if (!workspaceKey) throw new Error('Workspace is required')
  return `/workspaces/${encodeURIComponent(workspaceKey)}${suffix}`
}

async function authFetch(path: string, options?: RequestInit): Promise<Response> {
  const res = await fetch(`${API_BASE}${path}`, { credentials: 'include', ...options })
  if (res.status === 401) {
    window.location.href = '/auth/login'
    throw new Error('Session expired')
  }
  return res
}

async function failFriendly(res: Response, fallback: string): Promise<never> {
  let msg = fallback
  try {
    const body = await res.json()
    if (body?.error && typeof body.error === 'string') msg = body.error
  } catch {
    void 0
  }
  throw new Error(msg)
}

export interface LoopWrite {
  workspace_key: string
  agent_name: string
  frequency: 'daily' | 'weekly' | 'monthly'
  hour: number
  minute: number
  day_of_week?: number | null
  day_of_month?: number | null
  timezone: string
  max_runs: number
  max_tokens?: number | null
  idle_break: boolean
  enabled?: boolean
}

export async function createLoop(body: LoopWrite): Promise<{ id: string }> {
  const res = await authFetch(workspacePath(body.workspace_key, '/loops'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) await failFriendly(res, "Couldn't create the loop.")
  return res.json()
}

export async function updateLoop(id: string, body: LoopWrite): Promise<void> {
  const res = await authFetch(workspacePath(body.workspace_key, `/loops/${id}`), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) await failFriendly(res, "Couldn't update the loop.")
}

export async function setLoopEnabled(workspaceKey: string, id: string, enabled: boolean): Promise<void> {
  const res = await authFetch(workspacePath(workspaceKey, `/loops/${id}/enabled`), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled }),
  })
  if (!res.ok) await failFriendly(res, "Couldn't update the loop.")
}

export async function deleteLoop(workspaceKey: string, id: string): Promise<void> {
  const res = await authFetch(workspacePath(workspaceKey, `/loops/${id}`), { method: 'DELETE' })
  if (!res.ok) await failFriendly(res, "Couldn't delete the loop.")
}

export async function startLoopNow(workspaceKey: string, id: string): Promise<void> {
  const res = await authFetch(workspacePath(workspaceKey, `/loops/${id}/start-now`), { method: 'POST' })
  if (!res.ok) await failFriendly(res, "Couldn't start the loop.")
}

export async function breakLoopNow(workspaceKey: string, id: string): Promise<void> {
  const res = await authFetch(workspacePath(workspaceKey, `/loops/${id}/break-now`), { method: 'POST' })
  if (!res.ok) await failFriendly(res, "Couldn't stop the loop.")
}
